import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { endTour, isTourOpen, markTourShown, startTour, tourSeen, useTourOpen } from '../lib/tour';

/**
 * The first-visit tour: six things worth knowing, pointed at where they live.
 *
 * **This reverses a line in the requirements** ("four days, then never opened
 * again — no onboarding tour"), on request; see D68 for why it survives the
 * argument that line made. It keeps that line's spirit by being short — one
 * card per thing, one button per card, skippable from the first.
 *
 * **One tour, two devices, decided by width rather than by sniffing.** The
 * steps are the same on a phone and a laptop because the destinations are: the
 * header carries the same five tabs at both widths and the Add Photo button is
 * in the same corner. What differs is the verb ("tap" or "click") and where
 * the card sits — full width and above or below the spotlight on a phone, a
 * narrow card beside it on a laptop. 900px is the same line the Now page uses
 * for its two-column shell, so "laptop copy" and "laptop layout" never
 * disagree about which one the guest is looking at.
 *
 * **A step whose target is not on screen is skipped, not shown floating.** The
 * hero event card is gone once the week is over, and an organiser looking at a
 * guest with no roster row has no trip card. A card pointing at nothing is
 * worse than one fewer card.
 */

type Device = 'phone' | 'laptop';

interface Step {
  /** `data-tour` value of what to point at. Absent for the opening card. */
  target?: string;
  title: string;
  body: (d: Device) => string;
}

const verb = (d: Device) => (d === 'phone' ? 'Tap' : 'Click');

/**
 * In the order a guest reads Now on a phone: their own card first, then what
 * is happening, then the header left to right, then the button in the corner.
 *
 * Exported for the test, which checks every target here exists somewhere in
 * the app — a renamed attribute would otherwise quietly drop its step.
 */
export const STEPS: Step[] = [
  {
    title: 'Hi!',
    body: () => 'Let us show you where everything is.',
  },
  {
    target: 'seating',
    title: 'Your table',
    body: () => 'Where you’re sitting, and your room.',
  },
  {
    target: 'events',
    title: 'What’s next',
    body: (d) => `${verb(d)} for directions and what to wear.`,
  },
  {
    target: 'schedule',
    title: 'Schedule',
    body: () => 'The whole week, day by day.',
  },
  {
    target: 'map',
    title: 'Map',
    body: (d) => `The resort’s big. ${verb(d)} a pin to find your way.`,
  },
  {
    target: 'photos',
    title: 'Photos',
    body: () => 'Everyone’s photos from the week.',
  },
  {
    target: 'add-photo',
    title: 'Add yours',
    body: (d) => (d === 'phone' ? 'Straight from your camera roll.' : 'Straight from your computer.'),
  },
];

const LAPTOP = '(min-width: 900px)';
const PAD = 6;
const GAP = 12;
const GUTTER = 16;
const CARD_WIDTH = 340;

const deviceNow = (): Device =>
  (typeof window !== 'undefined' && window.matchMedia?.(LAPTOP).matches ? 'laptop' : 'phone');

const targetOf = (step: Step) =>
  (step.target ? document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`) : null);

interface Box { top: number; left: number; width: number; height: number }

const sameBox = (a: Box | null, b: Box | null) =>
  a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

/**
 * Mounted in the app shell. Opens itself on the first visit to Now, and only
 * there: a guest who arrived by a link to an event or a photograph came for
 * that, and a tour on top of it is in the way. They get it the first time they
 * land on Now instead.
 */
export default function Tour() {
  const open = useTourOpen();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === '/now' && !tourSeen() && !isTourOpen()) startTour();
  }, [pathname]);

  if (!open || pathname !== '/now') return null;
  return <TourCards />;
}

function TourCards() {
  const [device, setDevice] = useState<Device>(deviceNow);
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const primary = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLDivElement>(null);

  // The steps that have something to point at *now*. Recomputed per move
  // rather than once, because Now renders in pieces — the trip card arrives
  // with `/me`, the hero card with the schedule.
  const present = useCallback(
    () => STEPS.map((s, i) => ({ s, i })).filter(({ s }) => !s.target || targetOf(s)).map(({ i }) => i),
    [],
  );

  // Seen as soon as it is on screen, not when it is closed — see
  // `markTourShown`. A reload mid-tour must not bring it back.
  useEffect(() => { markTourShown(); }, []);

  const step = STEPS[index];
  const shown = present();
  const position = Math.max(0, shown.indexOf(index));
  const last = position >= shown.length - 1;

  const go = (dir: 1 | -1) => {
    const order = present();
    const next = order[order.indexOf(index) + dir];
    if (next === undefined) {
      if (dir === 1) endTour();
      return;
    }
    setIndex(next);
  };

  useEffect(() => {
    const mq = window.matchMedia?.(LAPTOP);
    if (!mq) return;
    const fn = () => setDevice(mq.matches ? 'laptop' : 'phone');
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  // Bring the target into view, but only if it is not already. The header and
  // the button are fixed, and scrolling the page for those would move the
  // content behind the spotlight for no reason.
  useLayoutEffect(() => {
    const el = targetOf(step);
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 64 || r.bottom > window.innerHeight - 80) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [step]);

  // Follow the target every frame while open. Smooth scrolling, the trip card
  // arriving late, a rotated phone and the browser toolbar collapsing all move
  // it, and one loop is simpler than listening for each. It only sets state
  // when the box actually changed, so a still page renders nothing.
  useEffect(() => {
    let frame = 0;
    let prev: Box | null = null;
    const tick = () => {
      const el = targetOf(step);
      const r = el?.getBoundingClientRect();
      const next = r && r.width > 0
        ? { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 }
        : null;
      if (!sameBox(prev, next)) { prev = next; setBox(next); }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [step]);

  useEffect(() => { primary.current?.focus({ preventScroll: true }); }, [index]);

  // `go` closes over the current step, so the listener reads it through a ref
  // and is added once rather than on every frame the spotlight moves.
  const goRef = useRef(go);
  goRef.current = go;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') endTour();
      else if (e.key === 'ArrowRight') goRef.current(1);
      else if (e.key === 'ArrowLeft') goRef.current(-1);
      else if (e.key === 'Tab') trapFocus(e, card.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // If a step lost its target between moves (say the week ended mid-tour),
  // move on rather than strand the guest on a card pointing at nothing.
  useEffect(() => {
    if (step.target && !targetOf(step)) go(1);
  });

  const style = cardStyle(box, device);

  return (
    <div className="tour" role="presentation">
      {/* Swallows taps so the page underneath cannot be navigated mid-tour; the
          dimming is the spotlight's own shadow, which leaves a hole over the
          target that a backdrop colour could not. */}
      <div className="tour-backdrop" onClick={(e) => e.stopPropagation()} />
      {box
        ? <div className="tour-spot" style={box} aria-hidden="true" />
        : <div className="tour-dim" aria-hidden="true" />}

      <div
        ref={card}
        className={`tour-card tour-card-${device}${box ? '' : ' tour-card-center'}`}
        style={style}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
      >
        <div className="tour-dots" aria-label={`Step ${position + 1} of ${shown.length}`}>
          {shown.map((i) => <span key={i} className={i === index ? 'on' : undefined} />)}
        </div>
        <h4 id="tour-title">{step.title}</h4>
        <p id="tour-body">{step.body(device)}</p>
        <div className="tour-actions">
          {position === 0
            ? <button type="button" className="btn btn-ghost" onClick={endTour}>Skip</button>
            : <button type="button" className="btn btn-ghost" onClick={() => go(-1)}>Back</button>}
          <div style={{ flex: 1 }} />
          <button
            ref={primary}
            type="button"
            className="btn btn-primary"
            onClick={() => (last ? endTour() : go(1))}
          >
            {position === 0 ? 'Show me' : last ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Above or below the spotlight, whichever side of the screen it is not on.
 *
 * Measured from the target's centre rather than from the card's height, so the
 * card never needs measuring first and never jumps on its first frame. On a
 * phone it is full width less the gutters, which keeps it clear of the last
 * 60px when it sits below (it only does that for targets in the top half) and
 * keeps a 44px button a 44px button.
 */
function cardStyle(box: Box | null, device: Device): React.CSSProperties | undefined {
  if (!box) return undefined;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = box.top + box.height / 2 < vh / 2;
  const vertical = below ? { top: box.top + box.height + GAP } : { bottom: vh - box.top + GAP };

  if (device === 'phone') return { ...vertical, left: GUTTER, right: GUTTER };

  const width = Math.min(CARD_WIDTH, vw - GUTTER * 2);
  const centred = box.left + box.width / 2 - width / 2;
  return { ...vertical, width, left: Math.min(Math.max(centred, GUTTER), vw - width - GUTTER) };
}

/**
 * Keep Tab inside the card. `aria-modal` promises the page behind is out of
 * reach, and the backdrop only keeps that promise for a pointer: without this
 * a keyboard user tabs straight into the header tabs the spotlight is pointing
 * at and leaves the tour half-finished.
 */
function trapFocus(e: KeyboardEvent, card: HTMLElement | null) {
  if (!card) return;
  const items = [...card.querySelectorAll<HTMLElement>('button:not(:disabled)')];
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (!card.contains(active)) { e.preventDefault(); first.focus(); }
  else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
}
